import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Package, RefreshCw } from "lucide-react";
import { waLink } from "@/lib/api";
import { PayPalButton } from "@/components/PayPalButton";

export function ProductsSection({ products, content }) {
  const categories = ["Todos", ...new Set(products.map((p) => p.category))];
  const [active, setActive] = useState("Todos");
  const filtered = active === "Todos" ? products : products.filter((p) => p.category === active);

  return (
    <section id="productos" className="py-24 lg:py-32 relative" data-testid="products-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[40rem] h-[40rem] rounded-full bg-emerald-500/6 blur-[160px]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-10 flex flex-wrap items-end justify-between gap-6">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-medium">Catálogo</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-3">
              Suplementos <span className="text-emerald-gradient">premium</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="category-filter-bar">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-1.5 rounded-full text-sm border transition-colors duration-200 ${active === c ? "bg-emerald-500 text-black border-emerald-500 font-semibold" : "border-emerald-500/25 text-gray-400 hover:text-emerald-400"}`}
              >
                {c}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <motion.article
                layout
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className="group rounded-2xl overflow-hidden border border-emerald-500/15 bg-[#0D130F] hover:border-emerald-500/40 hover:-translate-y-1 hover:glow-emerald transition-[border-color,transform,box-shadow] duration-300 flex flex-col"
                data-testid={`product-card-${p.id}`}
              >
                <div className="relative overflow-hidden aspect-[4/3]">
                  {p.image ? (
                    <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-emerald-500/5">
                      <Package className="w-10 h-10 text-emerald-500/40" />
                    </div>
                  )}
                  <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.2em] bg-black/70 backdrop-blur px-3 py-1 rounded-full text-yellow-500 border border-yellow-600/30">{p.category}</span>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-display text-lg font-bold tracking-tight">{p.name}</h3>
                  <p className="text-sm text-gray-400 mt-2 leading-relaxed flex-1">{p.description}</p>
                  {p.ingredients && <p className="text-[11px] font-mono text-emerald-500/70 mt-3">{p.ingredients}</p>}
                  <div className="flex items-end justify-between mt-4">
                    <div>
                      {p.priceOriginal && p.price ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-display text-xl font-extrabold text-gold-gradient" data-testid={`product-price-${p.id}`}>${p.price} {p.currency}</span>
                          <span className="text-sm text-gray-500 line-through">${p.priceOriginal}</span>
                          <span className="text-[10px] font-bold uppercase tracking-wider bg-yellow-600/20 text-yellow-500 border border-yellow-600/40 rounded-full px-2 py-0.5" data-testid={`product-discount-${p.id}`}>
                            -{Math.max(1, Math.round((1 - parseFloat(p.price) / parseFloat(p.priceOriginal)) * 100))}%
                          </span>
                        </div>
                      ) : (
                        <span className="font-display text-xl font-extrabold text-gold-gradient" data-testid={`product-price-${p.id}`}>{p.price ? `$${p.price} ${p.currency}` : "Consultar"}</span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">{p.stock > 0 ? `${p.stock} disp.` : "Agotado"}</span>
                  </div>
                  {p.priceSubscription && (
                    <div className="mt-2 flex items-center gap-2 rounded-lg bg-emerald-500/10 border border-emerald-500/25 px-3 py-1.5" data-testid={`product-subscription-${p.id}`}>
                      <RefreshCw className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      <span className="text-xs text-emerald-300">
                        Suscríbete y paga <span className="font-bold text-emerald-400">${p.priceSubscription} {p.currency}/mes</span>
                      </span>
                    </div>
                  )}
                  <a
                    href={waLink(content.whatsapp, `Hola, me interesa ${p.name}${p.price ? ` en oferta a $${p.price} ${p.currency}` : ""}. ¿Me das más información?`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`product-whatsapp-btn-${p.id}`}
                    className="mt-4 flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-sm py-2.5 rounded-full transition-colors duration-200"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Comprar por WhatsApp
                  </a>
                  {p.paypalButtonId && <PayPalButton hostedButtonId={p.paypalButtonId} />}
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
